import config from '../config';

/**
 * Get the JWT token from localStorage
 */
const getToken = () => {
  return localStorage.getItem('vt_dashboard_token');
};

/**
 * Make an authenticated API request
 */
const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const url = `${config.API_BASE_URL}${endpoint}`;
  const requestConfig = {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const response = await fetch(url, requestConfig);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `API request failed with status ${response.status}`);
  }

  return response.json();
};

/**
 * API methods
 */
export const api = {
  /**
   * Get all customers for the authenticated user
   */
  getCustomers: async () => {
    return apiRequest('/customers', { method: 'GET' });
  },

  /**
   * Get user profile
   */
  getUserProfile: async () => {
    return apiRequest('/user', { method: 'GET' });
  },

  /**
   * Create a new customer
   */
  createCustomer: async (email) => {
    return apiRequest('/customers', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Get customer files for a specific customer
   */
  getCustomerFiles: async (customerId) => {
    return apiRequest(`/customers/${customerId}/files`, { method: 'GET' });
  },

  /**
   * Get age gate configuration
   */
  getAgeGateConfig: async () => {
    return apiRequest('/age-gate/config', { method: 'GET' });
  },

  /**
   * Generate new API keys
   */
  generateApiKeys: async () => {
    return apiRequest('/age-gate/generate-api-keys', { method: 'POST' });
  },

  /**
   * Update age gate configuration (redirect URLs only)
   */
  updateAgeGateConfig: async (config) => {
    return apiRequest('/age-gate/config', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  },

  /**
   * Create Stripe checkout session for the current user (referenceId = user id or account reference)
   */
  createCheckoutSession: async (referenceId) => {
    return apiRequest(`/api/stripe-payment/reference/${referenceId}/checkout-session`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  /**
   * Get payment status by reference (e.g. user id)
   */
  getPaymentStatusByReference: async (referenceId) => {
    return apiRequest(`/api/stripe-payment/reference/${referenceId}/payment-status`, { method: 'GET' });
  },

  /**
   * Get billing status (plans, subscription, usage). For tiered subscriptions.
   */
  getBillingStatus: async () => {
    return apiRequest('/api/billing/status', { method: 'GET' });
  },

  /**
   * Create checkout session for subscription (tier = developer | startup | growth). Returns { url }.
   */
  createBillingCheckout: async (tier) => {
    return apiRequest('/api/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ tier }),
    });
  },

  /**
   * Create Customer Portal session URL. Returns { url }.
   */
  createBillingPortalSession: async () => {
    return apiRequest('/api/billing/portal-session', { method: 'POST', body: JSON.stringify({}) });
  },

};

export default api;
